import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Stack, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, IconButton,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon, Warning as WarningIcon,
  CheckCircle as CheckIcon, Cancel as CancelIcon, Payment as PayIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import PaginatedTable from '../components/common/PaginatedTable';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
  accountsReceivableApi,
  AccountReceivable,
  AccountReceivableStatus,
  AccountsReceivableSummary,
} from '../api/accounts-receivable.api';

const money = (n: any) => `S/ ${Number(n || 0).toFixed(2)}`;
const fdate = (d: any) => (d ? new Date(d).toLocaleDateString('es-PE') : '—');

const STATUS_LABELS: Record<AccountReceivableStatus, string> = {
  pendiente: 'Pendiente',
  vencido: 'Vencida',
  pagado: 'Pagada',
  cancelado: 'Cancelada',
};

const STATUS_COLORS: Record<AccountReceivableStatus, 'warning' | 'error' | 'success' | 'default'> = {
  pendiente: 'warning',
  vencido: 'error',
  pagado: 'success',
  cancelado: 'default',
};

const PAY_METHODS = [
  { key: 'efectivo', label: 'Efectivo' },
  { key: 'visa', label: 'Visa' },
  { key: 'mastercard', label: 'Mastercard' },
  { key: 'yape', label: 'Yape' },
  { key: 'plin', label: 'Plin' },
  { key: 'transferencia', label: 'Transfer.' },
];

const CuentasPorCobrar: React.FC = () => {
  const [rows, setRows] = useState<AccountReceivable[]>([]);
  const [summary, setSummary] = useState<AccountsReceivableSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<AccountReceivableStatus | 'todas'>('todas');
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Pago
  const [paying, setPaying] = useState<AccountReceivable | null>(null);
  const [payMethod, setPayMethod] = useState('efectivo');
  const [payRef, setPayRef] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Cancelar
  const [cancelling, setCancelling] = useState<AccountReceivable | null>(null);
  const [cancelNotes, setCancelNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [arRes, sumRes] = await Promise.all([
        accountsReceivableApi.getAll({
          status: filterStatus === 'todas' ? undefined : filterStatus,
          overdueOnly,
        }),
        accountsReceivableApi.getSummary(),
      ]);
      setRows(arRes.data || []);
      setSummary(sumRes.data);
    } catch (err) {
      console.error('Error cargando cuentas por cobrar:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, overdueOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const confirmPay = async () => {
    if (!paying) return;
    setSubmitting(true);
    try {
      await accountsReceivableApi.pay(paying.id, {
        method: payMethod,
        reference: payRef || undefined,
      });
      setPaying(null);
      setPayMethod('efectivo');
      setPayRef('');
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'No se pudo registrar el pago.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelling) return;
    setSubmitting(true);
    try {
      await accountsReceivableApi.cancel(cancelling.id, cancelNotes || undefined);
      setCancelling(null);
      setCancelNotes('');
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'No se pudo cancelar.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && rows.length === 0) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <WalletIcon sx={{ fontSize: 32, color: '#D97706' }} />
        <Typography variant="h4">Cuentas por Cobrar</Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
        Deudas generadas por cobros a credito. Vencidas requieren accion inmediata.
      </Typography>

      {/* KPIs */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>Pendiente total</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {money(summary?.totalPendiente)}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper sx={{ p: 1.5, borderLeft: '4px solid #DC2626' }}>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>Vencido</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#DC2626' }}>
              {money(summary?.totalVencido)}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>Deudas activas</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {summary?.countPendientes ?? 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>Vencidas</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#DC2626' }}>
              {summary?.countVencidos ?? 0}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Top deudores */}
      {summary && summary.topDebtors.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Top deudores
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {summary.topDebtors.map((d) => (
              <Chip
                key={d.customer.id}
                label={`${d.customer.name} · ${money(d.total)} (${d.count})`}
                color="warning" variant="outlined"
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* Filtros */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <ToggleButtonGroup
          value={filterStatus} exclusive size="small"
          onChange={(_e, v) => v && setFilterStatus(v)}
        >
          <ToggleButton value="todas">Todas</ToggleButton>
          <ToggleButton value="pendiente">Pendientes</ToggleButton>
          <ToggleButton value="vencido">Vencidas</ToggleButton>
          <ToggleButton value="pagado">Pagadas</ToggleButton>
          <ToggleButton value="cancelado">Canceladas</ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant={overdueOnly ? 'contained' : 'outlined'}
          color="error" startIcon={<WarningIcon />}
          onClick={() => setOverdueOnly((v) => !v)}
        >
          Solo vencidas
        </Button>
      </Stack>

      <PaginatedTable
        columns={[
          { id: 'id', label: '#', minWidth: 70, format: (v: number) => `#${v}` },
          {
            id: 'customer',
            label: 'Cliente',
            format: (_v: any, r: AccountReceivable) =>
              `${r.customer?.name ?? '—'} (${r.customer?.document ?? '?'})`,
          },
          {
            id: 'orderId',
            label: 'Pedido',
            format: (_v: any, r: AccountReceivable) => r.order?.code || `#${r.orderId}`,
          },
          { id: 'amount', label: 'Monto', format: (v: any) => money(v) },
          { id: 'dueDate', label: 'Vence', format: (v: string) => fdate(v) },
          {
            id: 'status',
            label: 'Estado',
            format: (v: AccountReceivableStatus) => (
              <Chip
                size="small"
                label={STATUS_LABELS[v] || v}
                color={STATUS_COLORS[v] || 'default'}
              />
            ),
          },
          {
            id: 'actions',
            label: 'Acciones',
            format: (_v: any, r: AccountReceivable) => (
              <Stack direction="row" spacing={0.5}>
                {(r.status === 'pendiente' || r.status === 'vencido') && (
                  <>
                    <IconButton
                      size="small" color="success"
                      title="Cobrar"
                      onClick={() => setPaying(r)}
                    >
                      <PayIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small" color="error"
                      title="Cancelar"
                      onClick={() => setCancelling(r)}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
                {r.status === 'pagado' && <CheckIcon color="success" fontSize="small" />}
              </Stack>
            ),
          },
        ] as any}
        rows={rows}
      />

      {/* Dialogo cobrar */}
      <Dialog open={!!paying} onClose={() => !submitting && setPaying(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Registrar cobro
          {paying && (
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              {paying.customer?.name} · {money(paying.amount)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>
            El cobro requiere caja abierta. Se crea un Payment vinculado a la sesion actual.
          </Alert>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Metodo de pago</Typography>
          <ToggleButtonGroup
            value={payMethod} exclusive fullWidth size="small"
            onChange={(_e, v) => v && setPayMethod(v)}
          >
            {PAY_METHODS.map((m) => (
              <ToggleButton key={m.key} value={m.key}>{m.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            fullWidth size="small" label="Referencia (op./voucher)"
            value={payRef} onChange={(e) => setPayRef(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPaying(null)} disabled={submitting}>Cancelar</Button>
          <Button
            variant="contained" color="success" startIcon={<PrintIcon />}
            disabled={submitting}
            onClick={confirmPay}
          >
            {submitting ? 'Procesando...' : `Cobrar ${money(paying?.amount)}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo cancelar */}
      <Dialog open={!!cancelling} onClose={() => !submitting && setCancelling(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar deuda</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
            Anular la deuda sin cobrar. Queda registrado en el historial.
          </Alert>
          <TextField
            fullWidth size="small" label="Motivo" multiline rows={2}
            value={cancelNotes} onChange={(e) => setCancelNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCancelling(null)} disabled={submitting}>Volver</Button>
          <Button
            variant="contained" color="error" startIcon={<CancelIcon />}
            disabled={submitting}
            onClick={confirmCancel}
          >
            Confirmar cancelacion
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CuentasPorCobrar;
